import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { FadeIn } from "@/components/FadeIn";
import { RevealHeading } from "@/components/RevealHeading";
import { SectionLabel } from "@/components/SectionLabel";
import { useCalendly } from "@/components/CalendlyContext";
import { serviceCategories } from "@/data/services";

export default function Services() {
  const { openCalendly } = useCalendly();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <div data-testid="services-page">
      <SEO
        title="Services"
        description="Brand strategy, digital marketing, web design, lead generation, automation and growth consulting — everything your brand needs to grow."
        path="/services"
      />

      <section className="pt-40 pb-20 border-b border-white/10">
        <div className="container-custom">
          <SectionLabel>Services</SectionLabel>
          <RevealHeading
            tag="h1"
            lines={["A FULL GROWTH SYSTEM,", "NOT A LIST OF TASKS."]}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight text-white max-w-3xl"
          />
          <FadeIn delay={0.3}>
            <p className="mt-8 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
              Every service below can stand alone or connect into a single system built around your business.
            </p>
          </FadeIn>
        </div>
      </section>

      {serviceCategories.map((cat, catIndex) => {
        const isEven = catIndex % 2 === 0;
        return (
          <section
            key={cat.slug}
            id={cat.slug}
            data-testid={`service-category-${cat.slug}`}
            className="py-20 md:py-28 border-b border-white/10 scroll-mt-28"
          >
            <div className="container-custom">
              <FadeIn className="grid md:grid-cols-2 gap-10 md:gap-16 items-center mb-14 md:mb-20">
                <div className={`overflow-hidden ${isEven ? "md:order-1" : "md:order-2"}`}>
                  <img
                    src={cat.image}
                    alt={cat.title}
                    data-testid={`service-category-image-${cat.slug}`}
                    className="w-full h-[280px] md:h-[400px] object-cover"
                  />
                </div>
                <div className={isEven ? "md:order-2" : "md:order-1"}>
                  <span className="font-display text-sm text-violet-400">{cat.number}</span>
                  <p className="text-xs tracking-widest uppercase text-violet-400 font-bold mb-3 mt-3">{cat.kicker}</p>
                  <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight text-white mb-5">
                    {cat.title}
                  </h2>
                  <p className="text-zinc-400 leading-relaxed max-w-md">{cat.description}</p>
                </div>
              </FadeIn>

              <div>
                {cat.items.map((service, i) => (
                  <FadeIn key={service.title} delay={Math.min(i * 0.04, 0.24)}>
                    <div
                      data-testid={`service-detail-${cat.slug}-${i}`}
                      className="grid md:grid-cols-12 gap-6 md:gap-10 py-8 border-t border-white/10 items-start"
                    >
                      <div className="md:col-span-4">
                        <h3 className="font-display text-lg md:text-xl font-bold text-white leading-tight">{service.title}</h3>
                      </div>
                      <div className="md:col-span-8 grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">What It Is</p>
                          <p className="text-sm text-zinc-400 leading-relaxed">{service.what}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Why It Matters</p>
                          <p className="text-sm text-zinc-400 leading-relaxed">{service.why}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">What We Provide</p>
                          <p className="text-sm text-zinc-400 leading-relaxed">{service.provides}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest text-violet-400/80 mb-2">Potential Outcome</p>
                          <p className="text-sm text-zinc-300 leading-relaxed">{service.outcome}</p>
                        </div>
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-28 border-t border-white/10 bg-[#0A0A0A]">
        <div className="container-custom text-center max-w-2xl mx-auto">
          <FadeIn>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">Not sure where to start?</h2>
            <p className="text-zinc-500 mb-8">Results are directional and depend on your market, offer and execution — no outcome is guaranteed. Let's map the right approach for your business.</p>
            <button
              data-testid="services-page-book-call-btn"
              onClick={openCalendly}
              className="group inline-flex items-center gap-2 bg-white text-black font-bold text-xs tracking-widest uppercase px-8 py-4 hover:bg-violet-500 hover:text-white transition-colors duration-300"
            >
              Book A Strategy Call
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
